import { useMemo } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, type Dependency, type Milestone, type PortfolioDetail, type PortfolioSchedule, type ProjectDetail, type ProjectMember, type Schedule, type ScheduleRow, type Task, type TaskStatus } from '../api.js'
import { projectColor } from './projectColors.js'

/**
 * TaskSource: what the Gantt, Board and List views need, independent of where the
 * tasks come from. `useProjectSource` backs the single-project pages with the same
 * query keys and endpoints as before (so realtime invalidation keeps working); a
 * portfolio source can later feed the same views with tasks from many projects.
 */
export interface SourceProject {
  id: string; code: string; name: string; color: string | null
  canEdit: boolean            // task edits (== ProjectDetail.permissions.logTime today: member or better, not archived)
  canUpdate: boolean          // baseline / autoSchedule (== permissions.update)
  canManageStatuses: boolean  // column CRUD (== permissions.manageStatuses)
  canManageMembers: boolean   // used by ProjectTabs canApprove
  statuses: TaskStatus[]; members: ProjectMember[]; milestones: Milestone[]
  autoSchedule: boolean
  baseline: { takenAt: string; takenByName: string | null } | null
}
export interface ScheduleView {
  end: string | null; criticalIds: Set<string>; rows: Map<string, ScheduleRow>
  baselineOf: Map<string, { startsOn: string | null; dueOn: string | null; estimateHours: number | null }>
}
export type TaskRef = Pick<Task, 'id' | 'projectId'>
export type InvalidateWhat = 'tasks' | 'deps' | 'schedule' | 'detail'
export interface TaskSource {
  kind: 'project' | 'portfolio'; id: string
  status: 'pending' | 'error' | 'ready'; errorMessage?: string
  projects: SourceProject[]                       // exactly one in project mode
  projectOf: (projectId: string) => SourceProject | undefined
  canEdit: (projectId: string) => boolean
  canEditAny: boolean
  tasks: Task[]                                   // raw, includes kind==='checklist' (views filter as today)
  deps: Dependency[]
  schedule: ScheduleView | null
  milestones: (Milestone & { projectId: string })[]
  members: ProjectMember[]                        // union, de-duplicated by userId
  // writes: each performs the API call and invalidates the right query keys; views wrap them in useMutation for isPending/onError exactly as today
  patchTask: (t: TaskRef, body: Record<string, unknown>) => Promise<{ task: Task; rescheduled: { id: string; key: string; startsOn: string; dueOn: string }[] }>
  moveTask: (t: TaskRef, body: { statusId: string; afterTaskId: string | null; beforeTaskId: string | null }, patch?: Record<string, unknown>) => Promise<void>
  createTask: (projectId: string, body: Record<string, unknown>) => Promise<{ task: Task }>
  deleteTask: (t: TaskRef) => Promise<void>
  checkItem: (t: TaskRef, done: boolean) => Promise<void>
  bulkPatch: (tasks: TaskRef[], patch: Record<string, unknown>) => Promise<{ updated: number; errors: { id: string; message: string }[] }>   // groups by projectId and fans out to POST /projects/:pid/tasks/bulk
  addDependency: (pred: TaskRef, succ: TaskRef, extra?: { type?: string; lagDays?: number }) => Promise<void>
  removeDependency: (d: Dependency, projectId: string) => Promise<void>
  takeBaseline: (projectId: string) => Promise<void>
  setAutoSchedule: (projectId: string, v: boolean) => Promise<void>
  columns?: {   // undefined in portfolio mode
    create: (name: string, category: string) => Promise<void>
    remove: (statusId: string) => Promise<void>
    reorder: (statusId: string, position: number) => Promise<void>
    setOptimistic: (order: TaskStatus[]) => void
  }
  setTasksOptimistic: (fn: (tasks: Task[]) => Task[]) => void   // board optimistic move (setQueryData)
  invalidate: (projectId: string, what: InvalidateWhat[]) => void
}

const NO_TASKS: Task[] = []
const NO_DEPS: Dependency[] = []

/** Invalidate the per-project keys (and, when the project sits in a portfolio, the portfolio-level ones too). */
function invalidateKeys(qc: QueryClient, projectId: string, portfolioId: string | null | undefined, what: InvalidateWhat[]) {
  const keys: unknown[][] = []
  for (const w of what) {
    if (w === 'tasks') { keys.push(['tasks', projectId], ['task']); if (portfolioId) keys.push(['portfolio-tasks', portfolioId]) }
    if (w === 'deps') { keys.push(['deps', projectId]); if (portfolioId) keys.push(['portfolio-deps', portfolioId]) }
    if (w === 'schedule') { keys.push(['schedule', projectId]); if (portfolioId) keys.push(['portfolio-schedule', portfolioId]) }
    if (w === 'detail') { keys.push(['project', projectId]); if (portfolioId) keys.push(['portfolio', portfolioId]) }
  }
  for (const k of keys) void qc.invalidateQueries({ queryKey: k })
}

export function useProjectSource(projectId: string): TaskSource {
  const qc = useQueryClient()
  const detail = useQuery({ queryKey: ['project', projectId], queryFn: () => api<ProjectDetail>(`/projects/${projectId}`), retry: false })
  const tasksQ = useQuery({ queryKey: ['tasks', projectId], queryFn: () => api<{ tasks: Task[] }>(`/projects/${projectId}/tasks`) })
  const depsQ = useQuery({ queryKey: ['deps', projectId], queryFn: () => api<{ dependencies: Dependency[] }>(`/projects/${projectId}/dependencies`) })
  const schedQ = useQuery({ queryKey: ['schedule', projectId], queryFn: () => api<Schedule>(`/projects/${projectId}/schedule`) })
  const d = detail.data, sched = schedQ.data, isError = detail.isError, error = detail.error
  const tasks = tasksQ.data?.tasks, deps = depsQ.data?.dependencies

  return useMemo<TaskSource>(() => {
    const portfolioId = d?.project.portfolioId
    const invalidate = (pid: string, what: InvalidateWhat[]) => invalidateKeys(qc, pid, portfolioId, what)
    const projects: SourceProject[] = d ? [{
      id: d.project.id, code: d.project.code, name: d.project.name, color: null,
      canEdit: d.permissions.logTime, canUpdate: d.permissions.update, canManageStatuses: d.permissions.manageStatuses, canManageMembers: d.permissions.manageMembers,
      statuses: d.statuses, members: d.members, milestones: d.milestones,
      autoSchedule: sched?.autoSchedule ?? true,
      baseline: sched?.baseline ? { takenAt: sched.baseline.takenAt, takenByName: sched.baseline.takenByName } : null,
    }] : []
    const project = projects[0]
    const schedule: ScheduleView | null = sched ? {
      end: sched.projectEnd, criticalIds: new Set(sched.criticalIds),
      rows: new Map(sched.rows.map((r) => [r.taskId, r])),
      baselineOf: new Map((sched.baseline?.tasks ?? []).map((b) => [b.taskId, b])),
    } : null
    const canEdit = (pid: string) => (pid === projectId ? project?.canEdit : undefined) ?? false
    return {
      kind: 'project', id: projectId,
      status: isError ? 'error' : d ? 'ready' : 'pending',
      ...(isError ? { errorMessage: error instanceof Error ? error.message : 'Project not found' } : {}),
      projects,
      projectOf: (pid) => (pid === projectId ? project : undefined),
      canEdit,
      canEditAny: project?.canEdit ?? false,
      tasks: tasks ?? NO_TASKS,
      deps: deps ?? NO_DEPS,
      schedule,
      milestones: (d?.milestones ?? []).map((m) => ({ ...m, projectId })),
      members: d?.members ?? [],
      patchTask: async (t, body) => {
        const r = await api<{ task: Task; rescheduled: { id: string; key: string; startsOn: string; dueOn: string }[] }>(`/projects/${t.projectId}/tasks/${t.id}`, { method: 'PATCH', json: body })
        invalidate(t.projectId, ['tasks', 'schedule'])
        return r
      },
      moveTask: async (t, body, patch) => {
        await Promise.all([
          api<{ task: Task }>(`/projects/${t.projectId}/tasks/${t.id}/move`, { method: 'POST', json: { statusId: body.statusId, afterTaskId: body.afterTaskId ?? null, beforeTaskId: body.beforeTaskId ?? null } }),
          patch ? api(`/projects/${t.projectId}/tasks/${t.id}`, { method: 'PATCH', json: patch }) : Promise.resolve(null),
        ])
        invalidate(t.projectId, ['tasks'])
      },
      createTask: async (pid, body) => {
        const r = await api<{ task: Task }>(`/projects/${pid}/tasks`, { method: 'POST', json: body })
        invalidate(pid, ['tasks'])
        return r
      },
      deleteTask: async (t) => {
        await api(`/projects/${t.projectId}/tasks/${t.id}`, { method: 'DELETE' })
        invalidate(t.projectId, ['tasks', 'deps'])
      },
      checkItem: async (t, done) => {
        await api(`/projects/${t.projectId}/tasks/${t.id}/check`, { method: 'POST', json: { done } })
        invalidate(t.projectId, ['tasks'])
      },
      bulkPatch: async (ts, patch) => {
        const byProject = new Map<string, string[]>()
        for (const t of ts) byProject.set(t.projectId, [...(byProject.get(t.projectId) ?? []), t.id])
        const results = await Promise.all(Array.from(byProject, ([pid, ids]) =>
          api<{ updated: number; errors: { id: string; message: string }[] }>(`/projects/${pid}/tasks/bulk`, { method: 'POST', json: { ids, patch } })))
        for (const pid of byProject.keys()) invalidate(pid, ['tasks'])
        return { updated: results.reduce((n, r) => n + r.updated, 0), errors: results.flatMap((r) => r.errors) }
      },
      addDependency: async (pred, succ, extra) => {
        await api(`/projects/${succ.projectId}/dependencies`, { method: 'POST', json: { predecessorId: pred.id, successorId: succ.id, ...(extra ?? {}) } })
        for (const pid of new Set([pred.projectId, succ.projectId])) invalidate(pid, ['deps'])
      },
      removeDependency: async (dep, pid) => {
        // A link into a sibling project is owned by the portfolio, not by either project.
        if (dep.predecessorProjectId !== dep.successorProjectId && portfolioId) await api(`/portfolios/${portfolioId}/dependencies/${dep.predecessorId}/${dep.successorId}`, { method: 'DELETE' })
        else await api(`/projects/${pid}/dependencies/${dep.predecessorId}/${dep.successorId}`, { method: 'DELETE' })
        for (const id of new Set([dep.predecessorProjectId, dep.successorProjectId])) invalidate(id, ['deps', 'schedule'])
      },
      takeBaseline: async (pid) => {
        await api(`/projects/${pid}/baseline`, { method: 'POST' })
        invalidate(pid, ['schedule'])
      },
      setAutoSchedule: async (pid, v) => {
        await api(`/projects/${pid}/schedule`, { method: 'PATCH', json: { autoSchedule: v } })
        invalidate(pid, ['schedule'])
      },
      columns: {
        create: async (name, category) => {
          await api(`/projects/${projectId}/statuses`, { method: 'POST', json: { name, category } })
          invalidate(projectId, ['tasks', 'detail'])
        },
        remove: async (statusId) => {
          await api(`/projects/${projectId}/statuses/${statusId}`, { method: 'DELETE' })
          invalidate(projectId, ['tasks', 'detail'])
        },
        reorder: async (statusId, position) => {
          await api(`/projects/${projectId}/statuses/${statusId}`, { method: 'PATCH', json: { position } })
          invalidate(projectId, ['tasks', 'detail'])
        },
        setOptimistic: (order) => qc.setQueryData<ProjectDetail>(['project', projectId], (old) => old ? { ...old, statuses: order } : old),
      },
      setTasksOptimistic: (fn) => qc.setQueryData<{ tasks: Task[] }>(['tasks', projectId], (old) => old ? { tasks: fn(old.tasks) } : old),
      invalidate,
    }
  }, [qc, projectId, d, sched, tasks, deps, isError, error])
}

/**
 * The portfolio source: every project the caller may see in the portfolio,
 * merged. Task writes still go to each task's own project (the per-project
 * routes check that project's role); links that cross projects go through
 * the portfolio. Columns are read-only here — statuses are managed in the
 * project. Project colours fall back to a palette entry by portfolio order.
 */
export function usePortfolioSource(portfolioId: string): TaskSource {
  const qc = useQueryClient()
  const detail = useQuery({ queryKey: ['portfolio', portfolioId], queryFn: () => api<PortfolioDetail>(`/portfolios/${portfolioId}`), retry: false })
  const tasksQ = useQuery({ queryKey: ['portfolio-tasks', portfolioId], queryFn: () => api<{ tasks: Task[]; hiddenProjects: number }>(`/portfolios/${portfolioId}/tasks`) })
  const depsQ = useQuery({ queryKey: ['portfolio-deps', portfolioId], queryFn: () => api<{ dependencies: Dependency[]; hiddenProjects: number }>(`/portfolios/${portfolioId}/dependencies`) })
  const schedQ = useQuery({ queryKey: ['portfolio-schedule', portfolioId], queryFn: () => api<PortfolioSchedule>(`/portfolios/${portfolioId}/schedule`) })
  const d = detail.data, sched = schedQ.data, isError = detail.isError, error = detail.error
  const tasks = tasksQ.data?.tasks, deps = depsQ.data?.dependencies

  return useMemo<TaskSource>(() => {
    const invalidate = (pid: string, what: InvalidateWhat[]) => invalidateKeys(qc, pid, portfolioId, what)
    const schedOf = new Map((sched?.projects ?? []).map((p) => [p.projectId, p]))
    const projects: SourceProject[] = (d?.projects ?? []).map((p, i) => ({
      id: p.id, code: p.code, name: p.name, color: p.color ?? projectColor(i),
      canEdit: p.permissions.manageTasks, canUpdate: p.permissions.update, canManageStatuses: false, canManageMembers: false,
      statuses: p.statuses, members: [], milestones: p.milestones,
      autoSchedule: schedOf.get(p.id)?.autoSchedule ?? p.autoSchedule,
      baseline: schedOf.get(p.id)?.baseline ? { takenAt: schedOf.get(p.id)!.baseline!.takenAt, takenByName: schedOf.get(p.id)!.baseline!.takenByName } : null,
    }))
    const byId = new Map(projects.map((p) => [p.id, p]))
    const schedule: ScheduleView | null = sched ? {
      end: sched.portfolioEnd, criticalIds: new Set(sched.criticalIds),
      rows: new Map(sched.rows.map((r) => [r.taskId, r])),
      baselineOf: new Map(sched.projects.flatMap((p) => (p.baseline?.tasks ?? []).map((b) => [b.taskId, b] as const))),
    } : null
    // People to filter by: the portfolio's members plus anyone a visible task is assigned to.
    const members = new Map<string, ProjectMember>((d?.members ?? []).map((m) => [m.userId, m]))
    for (const t of tasks ?? []) if (t.assigneeId && !members.has(t.assigneeId)) members.set(t.assigneeId, { userId: t.assigneeId, role: 'member', displayName: t.assigneeName ?? '?', email: '', avatarUrl: null, addedAt: '' })
    const canEdit = (pid: string) => byId.get(pid)?.canEdit ?? false
    const crossLink = (a: string, b: string) => a !== b
    return {
      kind: 'portfolio', id: portfolioId,
      status: isError ? 'error' : d ? 'ready' : 'pending',
      ...(isError ? { errorMessage: error instanceof Error ? error.message : 'Portfolio not found' } : {}),
      projects,
      projectOf: (pid) => byId.get(pid),
      canEdit,
      canEditAny: projects.some((p) => p.canEdit),
      tasks: tasks ?? NO_TASKS,
      deps: deps ?? NO_DEPS,
      schedule,
      milestones: (d?.projects ?? []).flatMap((p) => p.milestones.map((m) => ({ ...m, projectId: p.id }))),
      members: Array.from(members.values()).sort((a, b) => a.displayName.localeCompare(b.displayName)),
      patchTask: async (t, body) => {
        const r = await api<{ task: Task; rescheduled: { id: string; key: string; projectId: string; startsOn: string; dueOn: string }[] }>(`/projects/${t.projectId}/tasks/${t.id}`, { method: 'PATCH', json: body })
        for (const pid of new Set([t.projectId, ...r.rescheduled.map((m) => m.projectId)])) invalidate(pid, ['tasks', 'schedule'])
        return r
      },
      moveTask: async (t, body, patch) => {
        await Promise.all([
          api<{ task: Task }>(`/projects/${t.projectId}/tasks/${t.id}/move`, { method: 'POST', json: { statusId: body.statusId, afterTaskId: body.afterTaskId ?? null, beforeTaskId: body.beforeTaskId ?? null } }),
          patch ? api(`/projects/${t.projectId}/tasks/${t.id}`, { method: 'PATCH', json: patch }) : Promise.resolve(null),
        ])
        invalidate(t.projectId, ['tasks'])
      },
      createTask: async (pid, body) => {
        const r = await api<{ task: Task }>(`/projects/${pid}/tasks`, { method: 'POST', json: body })
        invalidate(pid, ['tasks'])
        return r
      },
      deleteTask: async (t) => {
        await api(`/projects/${t.projectId}/tasks/${t.id}`, { method: 'DELETE' })
        invalidate(t.projectId, ['tasks', 'deps'])
      },
      checkItem: async (t, done) => {
        await api(`/projects/${t.projectId}/tasks/${t.id}/check`, { method: 'POST', json: { done } })
        invalidate(t.projectId, ['tasks'])
      },
      bulkPatch: async (ts, patch) => {
        const byProject = new Map<string, string[]>()
        for (const t of ts) byProject.set(t.projectId, [...(byProject.get(t.projectId) ?? []), t.id])
        const results = await Promise.all(Array.from(byProject, ([pid, ids]) =>
          api<{ updated: number; errors: { id: string; message: string }[] }>(`/projects/${pid}/tasks/bulk`, { method: 'POST', json: { ids, patch } })))
        for (const pid of byProject.keys()) invalidate(pid, ['tasks'])
        return { updated: results.reduce((n, r) => n + r.updated, 0), errors: results.flatMap((r) => r.errors) }
      },
      addDependency: async (pred, succ, extra) => {
        const body = { predecessorId: pred.id, successorId: succ.id, ...(extra ?? {}) }
        if (crossLink(pred.projectId, succ.projectId)) await api(`/portfolios/${portfolioId}/dependencies`, { method: 'POST', json: body })
        else await api(`/projects/${succ.projectId}/dependencies`, { method: 'POST', json: body })
        for (const pid of new Set([pred.projectId, succ.projectId])) invalidate(pid, ['deps', 'schedule'])
      },
      removeDependency: async (dep, pid) => {
        if (crossLink(dep.predecessorProjectId, dep.successorProjectId)) await api(`/portfolios/${portfolioId}/dependencies/${dep.predecessorId}/${dep.successorId}`, { method: 'DELETE' })
        else await api(`/projects/${pid}/dependencies/${dep.predecessorId}/${dep.successorId}`, { method: 'DELETE' })
        for (const id of new Set([dep.predecessorProjectId, dep.successorProjectId])) invalidate(id, ['deps', 'schedule'])
      },
      takeBaseline: async (pid) => {
        await api(`/projects/${pid}/baseline`, { method: 'POST' })
        invalidate(pid, ['schedule'])
      },
      setAutoSchedule: async (pid, v) => {
        await api(`/projects/${pid}/schedule`, { method: 'PATCH', json: { autoSchedule: v } })
        invalidate(pid, ['schedule'])
      },
      setTasksOptimistic: (fn) => qc.setQueryData<{ tasks: Task[]; hiddenProjects: number }>(['portfolio-tasks', portfolioId], (old) => old ? { ...old, tasks: fn(old.tasks) } : old),
      invalidate,
    }
  }, [qc, portfolioId, d, sched, tasks, deps, isError, error])
}
